import { useCallback, useEffect, useRef } from "react";
import { playManagedSound, type ManagedSoundPlayback } from "../helpers/audio";
import { REWARD_COLLECTION_TIMING } from "../helpers/rewardCollection";

const FADE_MS = 150;

export function useRewardCollectionSound(
    presentationId: string | null,
    sounds: readonly string[],
) {
    const playbacksRef = useRef<ManagedSoundPlayback[]>([]);
    const fadeSound = useCallback(() => {
        for (const playback of playbacksRef.current) playback.cancel(FADE_MS);
    }, []);

    useEffect(() => {
        if (!presentationId || sounds.length === 0) return;
        const playbacks = sounds.map((sound, index) =>
            playManagedSound(sound, {
                delayMs: REWARD_COLLECTION_TIMING.revealDelayMs + index * 120,
                volume: sounds.length > 1 ? 0.7 : 1,
                fadeOutMs: FADE_MS,
                // Never queue a stale reward cue in a suspended audio context.
                getOffsetSeconds: () => 0,
            }),
        );
        playbacksRef.current = playbacks;
        const cancel = () => {
            for (const playback of playbacks) playback.cancel(0);
        };
        const onVisibilityChange = () => {
            if (document.hidden) cancel();
        };
        window.addEventListener("blur", cancel);
        document.addEventListener("visibilitychange", onVisibilityChange);
        return () => {
            cancel();
            if (playbacksRef.current === playbacks) playbacksRef.current = [];
            window.removeEventListener("blur", cancel);
            document.removeEventListener(
                "visibilitychange",
                onVisibilityChange,
            );
        };
    }, [presentationId, sounds]);

    return fadeSound;
}
